import { GetFullPathName, PreCertPaths } from "../Shared/Utility/PreCertLocationHelper";
import { IAuthService } from "./Auth/AuthService";
import { configService } from "./Config/ConfigService";

class AbpnClient {
    private _baseUri: string | undefined;

    private async ensureConfiguration() {
        if (this._baseUri) return;
        var configuration = await configService.getConfiguration();
        this._baseUri = configuration.PreCertApiUrl;
    }

    private async getDefaultHeaders(authContext: IAuthService) {
        const token = authContext?.isAuthenticated() ? await authContext.getAccessToken() : null;
        const headers = token ? [['Authorization', `Bearer ${token}`]] : [];
        headers.push(['Pragma', 'no-cache']);

        var onBehalfOf = authContext?.getOnBehalfOf();

        if (onBehalfOf) {
            headers.push(['X-OBO-PreCertUserId', onBehalfOf]);
        }

        var trainingProgramId = authContext.currentUser?.selectedTrainingProgramId;
        if (trainingProgramId) {
            headers.push(['X-TrainingProgramId', trainingProgramId.toString()]);
        }
        return headers;
    }

    private async validateResponse(response: Response): Promise<Response> {
        if (response.status === 401) {
            return Promise.reject(response.status);
        }
        if (!response.ok) {
            var clone = response.clone();
            const text = await clone.text();
            throw Error(`(${response.statusText}) ${text}}`);
        }
        return Promise.resolve(response);
    }

    private async castResponse<T>(response: Response): Promise<T | null> {
        const text = await response.text();
        if (!text) {
            return null;
        }
        return JSON.parse(text) as T;
    }

    private async handleError(error: any, authContext: IAuthService): Promise<any> {
        if (error === 401) {
            authContext.logout();
        } else {
            var path = PreCertPaths.ErrorPage;
            window.location.href = GetFullPathName(path)
            return Promise.reject(error);
        }
    }

    async post<T>(relativePath: string, body: any, authContext: any): Promise<T> {
        await this.ensureConfiguration();

        const headers = await this.getDefaultHeaders(authContext);
        headers.push(["Content-Type", "application/json"]);
        return fetch(this._baseUri + relativePath,
            {
                method: "post",
                headers: headers,
                body: JSON.stringify(body)
            })
            .then(response => this.validateResponse(response))
            .then(response => this.castResponse<T>(response))
            .catch(error => this.handleError(error, authContext));
    }

    async postForm<T>(relativePath: string, form: FormData, authContext: any) : Promise<T> {
        await this.ensureConfiguration();

        const headers = await this.getDefaultHeaders(authContext);
        headers.push(["Accept", "application/json, */*"]);
        return fetch(this._baseUri + relativePath,
        {
            method: 'POST',
            headers: headers,
            body: form
        })
        .then(response => this.validateResponse(response))
        .then(response => this.castResponse<T>(response))
        .catch(error => this.handleError(error, authContext));
    }

    async postStringResponse<T>(relativePath: string, body: any, authContext: any): Promise<T> {
        await this.ensureConfiguration();

        const headers = await this.getDefaultHeaders(authContext);
        headers.push(["Content-Type", "application/json"]);
        return fetch(this._baseUri + relativePath,
            {
                method: "post",
                headers: headers,
                body: JSON.stringify(body)
            })
            .then(response => this.validateResponse(response))
            .catch(error => this.handleError(error, authContext));
    }

    async get<T>(relativePath: string, authContext: any): Promise<T> {
        await this.ensureConfiguration();

        const headers = await this.getDefaultHeaders(authContext);

        return fetch(this._baseUri + relativePath, {
            method: "GET",
            headers: headers
          }).then(response => this.validateResponse(response))
            .then(response => this.castResponse<T>(response))
            .catch(error => this.handleError(error, authContext));
    }

    async getResponse(relativePath: string, authContext: any): Promise<any> {
        await this.ensureConfiguration();
        const headers = await this.getDefaultHeaders(authContext);

        return fetch(this._baseUri + relativePath, {
            headers: headers
        })
            .then(response => this.validateResponse(response))
            .catch(error => this.handleError(error, authContext));
    }
}
export const abpnClient = new AbpnClient();